// utils/timezones.js
//
// Robust helpers for timezone selects.
//
// • TIMEZONES            → ["Europe/London", …]
// • TIMEZONES_WITH_LABEL → [{ value: "Europe/London",
//                              label: "(UTC+00:00) Europe/London", offset: 0 }]
//
// Works in browsers and Node (>=16).  No NaNs ☺

/* ── 1. Build the raw list ───────────────────────────────────────────── */

let RAW_ZONES = [];

// Modern runtimes (Node 20 / evergreen browsers)
if (typeof Intl?.supportedValuesOf === "function") {
  RAW_ZONES = Intl.supportedValuesOf("timeZone");
} else {
  // Moment-timezone fallback
  try {
    // eslint-disable-next-line global-require
    const moment = require("moment-timezone");
    RAW_ZONES = moment?.tz?.names?.() || [];
  } catch {
    /* moment-timezone not installed */
  }
}

// Absolute fallback: fixed GMT offsets (27 zones incl. UTC±14 & UTC-12)
if (!RAW_ZONES.length) {
  RAW_ZONES = ["Etc/UTC", "Etc/GMT"];
  for (let h = 14; h >= -12; h -= 1) {
    const sign = h >= 0 ? "-" : "+";
    RAW_ZONES.push(`Etc/GMT${sign}${Math.abs(h)}`);
  }
}

/* ── 2. Helpers ──────────────────────────────────────────────────────── */

const now = new Date();

function zoneOffsetMinutes(zone) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const parts = dtf.formatToParts(new Date());
    const localH = parseInt(parts.find(p => p.type === 'hour').value, 10);
    const localM = parseInt(parts.find(p => p.type === 'minute').value, 10);

    const utcH = new Date().getUTCHours();
    const utcM = new Date().getUTCMinutes();

    // Calculate offset in minutes, accounting for wrap-around
    let offset = (localH - utcH) * 60 + (localM - utcM);
    if (offset > 720) offset -= 1440;  // wrap-around for timezones > UTC+12
    if (offset < -720) offset += 1440;

    return offset;
  } catch {
    return 0;
  }
}

function buildLabel(zone) {
  const minutes = zoneOffsetMinutes(zone);
  const sign = minutes >= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `(UTC${sign}${hh}:${mm}) ${zone}`;
}


/* ── 3. Final exports ───────────────────────────────────────────────── */

export const TIMEZONES = RAW_ZONES;

export const TIMEZONES_WITH_LABEL = TIMEZONES.map((z) => ({
  value: z,
  label: buildLabel(z),
  offset: zoneOffsetMinutes(z), // handy if you want to sort by offset
}));
